import {
	Text,
	Badge,
	Box,
	Popover,
	PopoverTrigger,
	PopoverContent,
	PopoverArrow,
	PopoverBody,
	Portal,
	Button,
} from "@chakra-ui/react";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { DateRangePicker, RangeKeyDict } from "react-date-range";
import colors from "utils/colors";
import { useCustomFormats } from "hooks/useCustomFormat";
import { useRef } from "react";

export type DateRangeType = {
	start: Date | undefined;
	end: Date | undefined;
};

type DateRangeSelectProps = {
	dateRange?: DateRangeType;
	setDateRange?: ({ start, end }: DateRangeType) => void;
	label?: string;
	emptyText?: string;
	hideIcon?: boolean;
};

const DateRangeSelect = ({
	dateRange = {
		start: new Date(),
		end: new Date(),
	},
	setDateRange,
	label,
	emptyText = "Choose Dates",
	hideIcon,
}: DateRangeSelectProps) => {
	const ref = useRef(null);

	const { dateFormat } = useCustomFormats();

	const key = "range";

	const onDateRangeChange = (val: RangeKeyDict) => {
		setDateRange?.({
			start: val[key].startDate,
			end: val[key].endDate,
		});
	};

	return (
		<Popover variant="responsive">
			<PopoverTrigger>
				<Button
					bg="transparent"
					textAlign="left"
					cursor="pointer"
					_hover={{
						bg: "transparent",
					}}
					py="8px"
					px="16px"
					borderRadius="7px"
					borderWidth="1.5px"
					fontSize="14px"
					fontWeight="500"
					color="gray.200"
					lineHeight="150%"
					borderColor="#D4D4D4"
					background="#fff"
					display="flex"
					alignItems="center"
					w={!label && emptyText !== "Funding Date" ? "100%" : "fit-content"}
				>
					{!!hideIcon && <FontAwesomeIcon icon={["fal", "calendar"]} />}
					{label && (
						<Text ml="10px" mr="0px" fontSize="14px" fontWeight="500">
							{label}
						</Text>
					)}
					{dateRange.start &&
					moment(dateRange.start).isValid() &&
					dateRange.end &&
					moment(dateRange.end).isValid() ? (
						<Badge
							bg="gray.50"
							borderRadius="10px"
							color="#7E858E"
							fontWeight="500"
							fontSize="14px"
							mx="8px"
							w={label ? "auto" : "100%"}
							px="12px"
							py="2px"
						>
							{moment(dateRange.start).format(dateFormat)} -{" "}
							{moment(dateRange.end).format(dateFormat)}
						</Badge>
					) : (
						<Text
							fontSize="14px"
							fontWeight="500"
							color="gray.200"
							lineHeight="150%"
							mr="5px"
							w={label ? "auto" : "100%"}
							px="4px"
						>
							{emptyText}
						</Text>
					)}

					<FontAwesomeIcon
						color={colors.GRAY_200}
						fontSize="12px"
						icon={["fas", "chevron-down"]}
					/>
				</Button>
			</PopoverTrigger>
			<Portal containerRef={ref}>
				<PopoverContent
					borderWidth="1px"
					borderColor="gray.50"
					overflow="hidden"
					boxShadow="0px 4px 4px rgba(204, 204, 204, 0.25), 0px 0px 15px rgba(198, 198, 198, 0.25)"
					borderRadius="20px"
				>
					<PopoverArrow />
					<PopoverBody p="0px" borderRadius="0px" borderWidth="0px">
						<DateRangePicker
							ranges={[
								{
									startDate: dateRange.start,
									endDate: dateRange.end,
									key,
								},
							]}
							minDate={moment().subtract(5, "years").toDate()}
							maxDate={moment().add(3, "years").toDate()}
							onChange={onDateRangeChange}
						/>
					</PopoverBody>
				</PopoverContent>
			</Portal>
			<Box ref={ref}></Box>
		</Popover>
	);
};

export default DateRangeSelect;